import dynamic from 'next/dynamic';
import { StaticImageData } from 'next/image';

import { ConfettiProps } from '~/components/common/confetti';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogTitle,
} from '~/components/common/responsive-dialog';
import { cn } from '~/lib/utils';

import GameResultDialogHeaderAnimation from './game-result-dialog-header-animation';

const Confetti = dynamic<ConfettiProps>(() => import('~/components/common/confetti').then((mod) => mod.Confetti), {
  ssr: false,
});

export default function GameResultDialog({
  open,
  setOpen,
  headerImage = null,
  headerContent,
  children,
  title,
  description,
  showConfetti = false,
  confettiAmount = 'default',
  result = 'won',
}: {
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  headerImage?: StaticImageData | null;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
  description?: string;
  showConfetti?: boolean;
  confettiAmount?: 'default' | 'excessive';
  result?: 'won' | 'lost';
}) {
  const won = result === 'won';

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen} dismissible={false}>
      <ResponsiveDialogContent className='p-0! gap-y-0 overflow-hidden border-0 md:max-w-xl'>
        <div
          className={cn(
            'hide-scrollbar max-h-[96svh] overflow-y-auto overscroll-none pb-5 md:pb-12',
            !open && 'opacity-0',
          )}
        >
          {won && <GameResultDialogHeaderAnimation headerContent={headerContent} image={headerImage} />}
          <div
            className={cn(
              'relative z-20 text-center',
              won ? '-mt-6 mb-4 md:-mt-11 md:mb-6' : 'bg-ultramarine mb-6 py-8 text-center text-white md:mb-8 md:py-10',
            )}
          >
            <div className='mx-auto max-w-lg px-8'>
              <ResponsiveDialogTitle className='font-greycliff-extrabold text-balance text-2xl md:text-3xl'>
                {title}
              </ResponsiveDialogTitle>
              {description && (
                <ResponsiveDialogDescription className='mt-2 text-base opacity-75'>
                  {description}
                </ResponsiveDialogDescription>
              )}
            </div>
            {!won && (
              <svg
                className='absolute inset-x-0 -bottom-px h-7 w-full md:h-10'
                width='672'
                height='48'
                viewBox='0 0 672 48'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                preserveAspectRatio='none'
                aria-hidden='true'
              >
                <path d='M0 0C0 0 107 47 336 47C565 47 672 0 672 0V48H0V0Z' fill='white' />
              </svg>
            )}
          </div>
          <div className='mx-auto w-full px-5 md:px-16'>{children}</div>
        </div>
      </ResponsiveDialogContent>
      {open && showConfetti && (
        <Confetti numberOfPieces={confettiAmount === 'excessive' ? 1000 : 500} recycle={false} />
      )}
    </ResponsiveDialog>
  );
}
